import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Database, BarChart3 } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import useOnPrem, { rowsOf } from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › UDFs — the desktop's OHIP › UDFs.
//
// v5 profile and reservation user-defined field values onto their Opera Cloud records:
//   1. Analyse — how many records carry a value in each of the 100 UDF columns, read from the property's v5 through
//      the project's SyncConnect (catalogue keys udfs / udf-usage-reservations). The ones in use are offered for mapping.
//   2. Map — each v5 UDF to the Cloud UDF it goes into (the same name by default). Remembered per property.
//   3. Load — the records with a value in a mapped UDF (udf-records-profiles / -reservations).
//   4. Post — one record per call (toolkit udfs.js): the Cloud record found by its v5 key and its UDFs PUT.

const today = () => new Date().toISOString().slice(0, 10);
const UDF_TYPE = { C: 'Character', N: 'Numeric', D: 'Date' };

const TABS = [
    { id: 'profile', label: 'Profiles', usage: 'udfs', records: 'udf-records-profiles', idKey: 'NAME_ID', idLabel: 'v5 Name ID',
      op: 'post-profile-udfs', text: 'Profiles with a reservation at the property.' },
    { id: 'reservation', label: 'Reservations', usage: 'udf-usage-reservations', records: 'udf-records-reservations',
      idKey: 'CONFIRMATION_NO', idLabel: 'v5 Confirmation', op: 'post-reservation-udfs',
      text: 'The property\'s reservations, cancelled and no-shows left out.' },
];

// A records row (lower-case keys) with the UDF columns and ids upper-cased, as udfs.js reads them.
const upper = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k.toUpperCase(), v]));

function ToolkitUdfsPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const { ask } = useOnPrem(ctx);
    const [tabId, setTabId] = useState(TABS[0].id);
    const tab = TABS.find((t) => t.id === tabId);
    const [usage, setUsage] = useState({ state: STEP.IDLE, message: '', counts: null, total: 0 });
    const [mapping, setMapping] = useState({});        // v5 field → { include, cloud }
    const [mapMsg, setMapMsg] = useState('');
    const [since, setSince] = useState(today());
    const [dateField, setDateField] = useState('INSERT_DATE');
    const [load, setLoad] = useState({ state: STEP.IDLE, message: '' });
    const configKey = `udf-mapping-${tabId}`;

    const chosen = useMemo(() => Object.fromEntries(Object.entries(mapping)
        .filter(([, m]) => m.include && m.cloud).map(([f, m]) => [f, m.cloud.trim()])), [mapping]);
    const chosenFields = Object.keys(chosen);

    const request = useCallback((row) => ({ area: 'udfs', op: tab.op, args: { record: sourceRow(row, ['values', 'sent', 'targetId']), mappings: chosen } }),
        [tab.op, chosen]);
    const batch = useToolkitBatch({ request, ctx,
        server: { screen: `udfs-${tab.id}`, title: `UDFs — ${tab.label}`, keyOf: (row) => row[tab.idKey] } });
    const { load: loadRows } = batch;

    // The remembered mapping for this property and record type.
    useEffect(() => {
        if (!ready) return;
        setMapping({});
        setMapMsg('');
        toolkitService.storage(ctx, 'get-config', { configKey })
            .then(({ data }) => {
                const saved = data.result?.value;
                if (saved && typeof saved === 'object') {
                    setMapping(Object.fromEntries(Object.entries(saved).map(([f, cloud]) => [f, { include: true, cloud }])));
                    setMapMsg('Mapping remembered for this property.');
                }
            })
            .catch(() => {});
    }, [ready, ctx, configKey]);

    const switchTab = (id) => {
        if (batch.running || id === tabId) return;
        setTabId(id);
        setUsage({ state: STEP.IDLE, message: '', counts: null, total: 0 });
        setLoad({ state: STEP.IDLE, message: '' });
        loadRows([]);
    };

    const analyse = async () => {
        setUsage({ state: STEP.BUSY, message: 'Asking SyncConnect which UDFs carry values…', counts: null, total: 0 });
        try {
            const d = await ask(tab.usage);
            if (d.status !== 'RETURNED') { setUsage({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}).`, counts: null, total: 0 }); return; }
            const row = upper(rowsOf(d.result)[0] || {});
            const total = Number(row.PROFILES ?? row.RESERVATIONS ?? 0);
            const counts = Object.entries(row).filter(([k, v]) => /^UDF[CDN]\d{2}$/.test(k) && Number(v) > 0)
                .map(([field, n]) => ({ field, n: Number(n) })).sort((a, b) => a.field.localeCompare(b.field));
            setMapping((m) => {
                const next = { ...m };
                for (const { field } of counts) if (!next[field]) next[field] = { include: true, cloud: field };
                return next;
            });
            setUsage({ state: STEP.DONE, counts, total,
                message: counts.length ? `${counts.length} UDF(s) in use across ${total} record(s).` : `No UDF carries a value on any of the ${total} record(s).` });
        } catch (e) {
            setUsage({ state: STEP.ERROR, message: e?.response?.data?.message || e.message, counts: null, total: 0 });
        }
    };

    const setField = (f, fields) => setMapping((m) => ({ ...m, [f]: { ...(m[f] || { include: true, cloud: f }), ...fields } }));

    const saveMapping = async () => {
        try {
            await toolkitService.storage(ctx, 'save-config', { configKey, value: chosen });
            setMapMsg(`Mapping of ${chosenFields.length} UDF(s) remembered for this property.`);
        } catch (e) {
            setMapMsg(e?.response?.data?.message || e.message);
        }
    };

    const loadRecords = async () => {
        setLoad({ state: STEP.BUSY, message: 'Asking SyncConnect for the UDF values…' });
        try {
            const params = { fields: chosenFields.join(','), sinceDate: since, ...(tabId === 'profile' ? { dateField } : {}) };
            const d = await ask(tab.records, params);
            if (d.status !== 'RETURNED') { setLoad({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}) — try again shortly.` }); return; }
            const rows = rowsOf(d.result).map(upper).filter((r) => r[tab.idKey]);
            loadRows(rows.map((r) => ({ ...r, values: chosenFields.filter((f) => r[f] != null && String(r[f]).trim() !== '').length })));
            await saveMapping();
            setLoad({ state: d.result_truncated === 'Y' ? STEP.ERROR : STEP.DONE,
                message: `${rows.length} record(s) with a mapped UDF value${d.result_truncated === 'Y' ? ' — the answer was cut short; move the date on and run again for the rest' : ''}.` });
        } catch (e) {
            setLoad({ state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    const columns = [
        { key: tab.idKey, label: tab.idLabel },
        { key: 'values', label: 'Values' },
        { key: 'sent', label: 'Sent' },
        { key: 'targetId', label: 'Cloud id' },
    ];
    const fields = Object.keys(mapping).sort();

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="UDFs"
                subtitle="Copy v5 profile and reservation UDF values onto their Opera Cloud records." />

            <div className="tkb-tabs">
                {TABS.map((t) => (
                    <button key={t.id} type="button" className={`tkb-tab${t.id === tabId ? ' tkb-tab-active' : ''}`}
                        onClick={() => switchTab(t.id)} disabled={batch.running}>{t.label}</button>
                ))}
            </div>
            <p className="tkb-hint">{tab.text}</p>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Analyse UDF usage {stepIcon(usage.state)}</div>
                <Button variant="secondary" onClick={analyse} disabled={!ready || usage.state === STEP.BUSY || batch.running}>
                    <BarChart3 className="icon" /> Analyse
                </Button>
                {usage.message && <p className={`tkb-msg${usage.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{usage.message}</p>}
            </div>

            {fields.length > 0 && (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">2</span> Map to Opera Cloud UDFs</div>
                    <div className="otx-table-wrap" style={{ maxWidth: 640 }}>
                        <table className="otx-table">
                            <thead><tr><th /><th>v5 UDF</th><th>Type</th><th>Records</th><th>Opera Cloud UDF</th></tr></thead>
                            <tbody>{fields.map((f) => {
                                const m = mapping[f];
                                const n = usage.counts?.find((c) => c.field === f)?.n;
                                return (
                                    <tr key={f}>
                                        <td><input type="checkbox" checked={m.include} onChange={(e) => setField(f, { include: e.target.checked })} /></td>
                                        <td><code>{f}</code></td>
                                        <td>{UDF_TYPE[f[3]]}</td>
                                        <td>{n ?? '—'}</td>
                                        <td><input type="text" value={m.cloud} onChange={(e) => setField(f, { cloud: e.target.value.toUpperCase() })} /></td>
                                    </tr>
                                );
                            })}</tbody>
                        </table>
                    </div>
                    <Button variant="secondary" onClick={saveMapping} disabled={!ready || !chosenFields.length} style={{ marginTop: '0.6rem' }}>Remember mapping</Button>
                    {mapMsg && <p className="tkb-msg">{mapMsg}</p>}
                </div>
            )}

            {fields.length > 0 && (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">3</span> Load the records {stepIcon(load.state)}</div>
                    <div className="tkb-row">
                        {tabId === 'profile' ? (
                            <>
                                <label><span>Profiles</span>
                                    <select value={dateField} onChange={(e) => setDateField(e.target.value)}>
                                        <option value="INSERT_DATE">created on or after</option>
                                        <option value="UPDATE_DATE">updated on or after</option>
                                    </select>
                                </label>
                                <label><span>Date</span><input type="date" value={since} onChange={(e) => setSince(e.target.value)} /></label>
                            </>
                        ) : (
                            <label><span>Departing on or after (go-live)</span><input type="date" value={since} onChange={(e) => setSince(e.target.value)} /></label>
                        )}
                        <Button variant="secondary" onClick={loadRecords} disabled={!ready || !chosenFields.length || !since || load.state === STEP.BUSY || batch.running}>
                            <Database className="icon" /> Load through SyncConnect
                        </Button>
                    </div>
                    {load.message && <p className={`tkb-msg${load.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{load.message}</p>}
                </div>
            )}

            <ToolkitBatchTable batch={batch} columns={columns} searchKeys={[tab.idKey]} exportName={`UDFs_${tabId}`}
                canRun={ready && chosenFields.length > 0} onClear={() => loadRows([])} />
        </div>
    );
}

export default ToolkitUdfsPage;
